// TAX2-S2 (docs/build-briefs/TAX2-S2-sales-tax-worksheet.md; DEC-303, DEC-304; design/mockups/
// tax2-1-september.html, tax2-2-rate-change.html): the rules behind the Sales tax worksheet screen,
// with no DOM, so each can be tested on its own. The server's worksheet (src/domain/
// salesTaxWorksheet.ts) is the only source of figures; nothing here works out tax. This module
// turns what it sent into the rows, words and states the screen shows.
//
// An internal worksheet that never files. Nothing here, and nothing on the screen, can mark a
// period as filed.

import { cutoffText, dayLabel, formatMoney } from "./reportFormat.ts";

// ---------------------------------------------------------------------
// The server's answer (mirrors src/domain/salesTaxWorksheet.ts and salesTaxWorksheetReport.ts)
// ---------------------------------------------------------------------

export type WorksheetBasis = "business_date" | "calendar_date";
export type PeriodKind = "month" | "quarter" | "year" | "custom";
export type FilingFrequency = "monthly" | "quarterly" | "annual";
export type TaxComponentName =
  | "state"
  | "county"
  | "transit"
  | "additional_county"
  | "city"
  | "special_district"
  | "unsplit";
export type DayFlag =
  | "payment_missing_tax"
  | "no_stored_rate"
  | "refund_missing_tax"
  | "unknown_refund"
  | "rate_changed";

export type ComponentTotals = Record<TaxComponentName, number>;

export interface WorksheetDayDto {
  date: string;
  grossReceiptsCents: number;
  discountsAndCompsCents: number;
  refundsCents: number;
  taxableReceiptsCents: number;
  taxCollectedCents: number;
  taxRefundedCents: number;
  netTaxCents: number;
  rateBasisPoints: number | null;
  flags: DayFlag[];
}

export interface CheckedRowsDto {
  count: number;
  amountCents: number;
  ids: string[];
}

export interface SalesTaxWorksheetDto {
  tenantId: string;
  locationId: string;
  from: string;
  to: string;
  basis: WorksheetBasis;
  timeZone: string;
  totals: {
    grossReceiptsCents: number;
    discountsAndCompsCents: number;
    compsCents: number;
    refundsCents: number;
    exemptReceiptsCents: number;
    taxableReceiptsCents: number;
    taxDueByComponent: ComponentTotals;
    totalTaxDueCents: number;
    taxCollectedCents: number;
    taxRefundedCents: number;
    netTaxCollectedCents: number;
    excessCollectedCents: number;
    collectedByComponent: ComponentTotals;
    collectedWithoutComponentsCents: number;
  };
  days: WorksheetDayDto[];
  otherBasis: {
    basis: WorksheetBasis;
    grossReceiptsCents: number;
    taxableReceiptsCents: number;
    totalTaxDueCents: number;
    taxCollectedCents: number;
    differs: boolean;
  };
  checks: {
    tiesToCloseouts: {
      pass: boolean;
      finalizedDaysChecked: number;
      mismatches: {
        businessDate: string;
        field: "gross_before_tax" | "tax" | "refund_amount" | "refund_tax";
        closeoutCents: number | null;
        computedCents: number;
      }[];
      daysWithoutRefundTax: string[];
    };
    tiesToPayments: {
      pass: boolean;
      differences: { field: string; dayRowsCents: number; paymentsCents: number }[];
    };
    paymentsMissingTax: CheckedRowsDto;
    paymentsWithoutStoredRate: CheckedRowsDto;
    refundsMissingTax: CheckedRowsDto;
    unknownRefunds: CheckedRowsDto;
    unresolvedPayments: CheckedRowsDto;
    unresolvedRefunds: CheckedRowsDto;
    timeZone: { zone: string; isUtc: boolean; warning: string | null };
    rateChangedMidPeriod: {
      changed: boolean;
      changes: { effectiveFrom: string; fromBasisPoints: number | null; toBasisPoints: number }[];
      distinctStoredRates: number[];
    };
  };
}

export interface SalesTaxContextDto {
  today: string;
  timezone: string;
  cutoffLocal: string;
  paymentsSimulated: boolean;
  location: { id: string; name: string };
  rateComponentsAtEnd: { component: TaxComponentName; basisPoints: number }[];
  isWholeMonth: boolean;
  /** TAX2-S2-F1: what the request named, and the location's saved defaults the worksheet opens on. */
  period: { kind: PeriodKind; key: string | null };
  filing: { frequency: FilingFrequency; dateBasis: WorksheetBasis };
}

export interface SalesTaxReportDto {
  worksheet: SalesTaxWorksheetDto;
  context: SalesTaxContextDto;
}

// ---------------------------------------------------------------------
// The E-500 lines, one constant per form year
// ---------------------------------------------------------------------

/**
 * The NC Form E-500 line each figure belongs to, ONE table per form year, with a `confirmed` flag per
 * entry. A line badge is printed ONLY where the line is confirmed against the current form; every other
 * figure shows its plain label with no line number, because a placeholder printed as fact on a tax
 * form is worse than none (designer review #12). Only the additional county line (12) was given
 * (design/mockups/tax2-notes.md); the rest are the design's placeholders, kept here unconfirmed so
 * turning a badge on is one edit once the owner's accountant has checked it (TAX2-S2-F2).
 */
export const E500_FORM_YEAR = 2026;
export interface E500LineEntry {
  line: number;
  confirmed: boolean;
}
export const E500_LINES = {
  gross: { line: 1, confirmed: false },
  exempt: { line: 2, confirmed: false },
  taxable: { line: 3, confirmed: false },
  state: { line: 9, confirmed: false },
  county: { line: 10, confirmed: false },
  transit: { line: 11, confirmed: false },
  additional_county: { line: 12, confirmed: true },
  totalDue: { line: 13, confirmed: false },
  excess: { line: 14, confirmed: false },
} as const satisfies Record<string, E500LineEntry>;

/** The badge text for a figure: "Line 12" where confirmed, otherwise empty (no badge is drawn). */
export function lineBadge(key: keyof typeof E500_LINES): string {
  const entry: E500LineEntry = E500_LINES[key];
  return entry.confirmed ? `Line ${entry.line}` : "";
}

// ---------------------------------------------------------------------
// Months
// ---------------------------------------------------------------------

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTH_PATTERN = /^(\d{4})-(\d{2})$/;

/** "2026-09-30" -> "2026-09". */
export function monthOfDate(dateIso: string): string {
  return dateIso.slice(0, 7);
}

/** "2026-12" plus 1 is "2027-01"; minus 1 from "2026-01" is "2025-12". */
export function shiftMonth(month: string, delta: number): string {
  const match = MONTH_PATTERN.exec(month);
  if (match === null) {
    throw new Error(`not a month: ${month}`);
  }
  const index = Number(match[1]) * 12 + (Number(match[2]) - 1) + delta;
  const year = Math.floor(index / 12);
  const monthNumber = (index % 12) + 1;
  return `${year}-${String(monthNumber).padStart(2, "0")}`;
}

/** "2026-09" -> "September 2026". */
export function monthLabel(month: string): string {
  const match = MONTH_PATTERN.exec(month);
  if (match === null) {
    throw new Error(`not a month: ${month}`);
  }
  return `${MONTH_NAMES[Number(match[2]) - 1] ?? ""} ${match[1]}`;
}

/** The next arrow is off for the month the business date is in: nothing later has happened. */
export function canShowNextMonth(month: string, today: string): boolean {
  return month < monthOfDate(today);
}

// ---------------------------------------------------------------------
// Periods (TAX2-S2-F1): a month, a calendar quarter, a calendar year, or two whole dates.
// Mirrors src/domain/salesTaxPeriods.ts, and a parity test sweeps both over the same dates. Every
// date is a whole calendar date in the location's time zone; nothing here reads a clock or a UTC instant.
// ---------------------------------------------------------------------

/** What the person has picked. `key` is "2026-07", "2026-Q3" or "2026"; a custom range carries its two dates. */
export interface PeriodSelection {
  kind: PeriodKind;
  key: string | null;
  from: string;
  to: string;
}

export const PERIOD_KINDS: readonly { kind: PeriodKind; label: string }[] = [
  { kind: "month", label: "Month" },
  { kind: "quarter", label: "Quarter" },
  { kind: "year", label: "Year" },
  { kind: "custom", label: "Custom" },
];

const QUARTER_PATTERN = /^(\d{4})-Q([1-4])$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** First and last day of a "YYYY-MM" month. */
export function monthBounds(month: string): { from: string; to: string } {
  const match = MONTH_PATTERN.exec(month);
  if (match === null) throw new Error(`not a month: ${month}`);
  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  return {
    from: `${match[1]}-${match[2]}-01`,
    to: `${match[1]}-${match[2]}-${pad2(lastDayOfMonth(year, monthNumber))}`,
  };
}

/** "2026-08-14" -> "2026-Q3". */
export function quarterOfDate(dateIso: string): string {
  return `${dateIso.slice(0, 4)}-Q${Math.floor((Number(dateIso.slice(5, 7)) - 1) / 3) + 1}`;
}

/** First and last day of a calendar quarter "YYYY-Qn". */
export function quarterBounds(quarter: string): { from: string; to: string } {
  const match = QUARTER_PATTERN.exec(quarter);
  if (match === null) throw new Error(`not a quarter: ${quarter}`);
  const year = Number(match[1]);
  const firstMonth = (Number(match[2]) - 1) * 3 + 1;
  const lastMonth = firstMonth + 2;
  return {
    from: `${year}-${pad2(firstMonth)}-01`,
    to: `${year}-${pad2(lastMonth)}-${pad2(lastDayOfMonth(year, lastMonth))}`,
  };
}

/** "2026-Q4" plus 1 is "2027-Q1"; minus 1 from "2026-Q1" is "2025-Q4". */
export function shiftQuarter(quarter: string, delta: number): string {
  const match = QUARTER_PATTERN.exec(quarter);
  if (match === null) throw new Error(`not a quarter: ${quarter}`);
  const index = Number(match[1]) * 4 + (Number(match[2]) - 1) + delta;
  return `${Math.floor(index / 4)}-Q${(index % 4) + 1}`;
}

/** "2026-Q3" -> "Q3 2026". */
export function quarterLabel(quarter: string): string {
  const match = QUARTER_PATTERN.exec(quarter);
  if (match === null) throw new Error(`not a quarter: ${quarter}`);
  return `Q${match[2]} ${match[1]}`;
}

export function yearBounds(year: string): { from: string; to: string } {
  if (!/^\d{4}$/.test(year)) throw new Error(`not a year: ${year}`);
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export function shiftYear(year: string, delta: number): string {
  if (!/^\d{4}$/.test(year)) throw new Error(`not a year: ${year}`);
  return String(Number(year) + delta);
}

/** The key of the month, quarter or year of the given kind that holds the date; custom has none. */
export function periodKeyContaining(kind: Exclude<PeriodKind, "custom">, dateIso: string): string {
  if (kind === "month") return monthOfDate(dateIso);
  if (kind === "quarter") return quarterOfDate(dateIso);
  return dateIso.slice(0, 4);
}

/** A selection for a month, quarter or year key. */
export function selectionFromKey(kind: Exclude<PeriodKind, "custom">, key: string): PeriodSelection {
  const bounds =
    kind === "month" ? monthBounds(key) : kind === "quarter" ? quarterBounds(key) : yearBounds(key);
  return { kind, key, ...bounds };
}

/** A custom selection from two whole dates. */
export function customSelection(from: string, to: string): PeriodSelection {
  return { kind: "custom", key: null, from, to };
}

/** What the server answered, as a selection. */
export function selectionFromReport(report: SalesTaxReportDto): PeriodSelection {
  const { kind, key } = report.context.period;
  if (kind === "custom" || key === null) {
    return customSelection(report.worksheet.from, report.worksheet.to);
  }
  return selectionFromKey(kind, key);
}

/** Switching kind keeps the period that holds the one on screen (its first day); Custom keeps the same two dates. */
export function switchKind(current: PeriodSelection, kind: PeriodKind): PeriodSelection {
  if (kind === "custom") return customSelection(current.from, current.to);
  return selectionFromKey(kind, periodKeyContaining(kind, current.from));
}

/** The previous or next month, quarter or year. A custom range has no arrows. */
export function shiftSelection(current: PeriodSelection, delta: number): PeriodSelection {
  if (current.kind === "custom" || current.key === null) return current;
  const key =
    current.kind === "month"
      ? shiftMonth(current.key, delta)
      : current.kind === "quarter"
        ? shiftQuarter(current.key, delta)
        : shiftYear(current.key, delta);
  return selectionFromKey(current.kind, key);
}

/** The next arrow is off when the next period has not started: nothing in it has happened. */
export function canShowNextSelection(current: PeriodSelection, today: string): boolean {
  if (current.kind === "custom") return false;
  return shiftSelection(current, 1).from <= today;
}

export function canShowPreviousSelection(current: PeriodSelection): boolean {
  return current.kind !== "custom";
}

/** The request's query for a selection: month, quarter, year, or from and to. */
export function periodQuery(current: PeriodSelection): Record<string, string> {
  if (current.kind === "month") return { month: current.key ?? monthOfDate(current.from) };
  if (current.kind === "quarter") return { quarter: current.key ?? quarterOfDate(current.from) };
  if (current.kind === "year") return { year: current.key ?? current.from.slice(0, 4) };
  return { from: current.from, to: current.to };
}

/** "September 2026", "Q3 2026", "2026", or "Jul 1 to Sep 30". */
export function selectionLabel(current: PeriodSelection): string {
  if (current.kind === "month") return monthLabel(current.key ?? monthOfDate(current.from));
  if (current.kind === "quarter") return quarterLabel(current.key ?? quarterOfDate(current.from));
  if (current.kind === "year") return current.key ?? current.from.slice(0, 4);
  return `${shortDay(current.from)} to ${shortDay(current.to)}`;
}

export function filingFrequencyKind(frequency: FilingFrequency): Exclude<PeriodKind, "custom"> {
  return frequency === "monthly" ? "month" : frequency === "quarterly" ? "quarter" : "year";
}

export function kindFilingFrequency(kind: PeriodKind): FilingFrequency | null {
  return kind === "month" ? "monthly" : kind === "quarter" ? "quarterly" : kind === "year" ? "annual" : null;
}

/** The kind's noun for the strip: "Month is closed", "Quarter is closed". */
export function periodNoun(kind: PeriodKind): string {
  return kind === "month" ? "Month" : kind === "quarter" ? "Quarter" : kind === "year" ? "Year" : "Period";
}

/** True when a custom pair of dates can be asked for: both whole dates, real, in order. */
export function customRangeAsk(from: string, to: string): boolean {
  const real = (value: string): boolean => {
    const match = DATE_PATTERN.exec(value);
    if (match === null) return false;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return date.toISOString().slice(0, 10) === value;
  };
  return real(from) && real(to) && from <= to;
}

/** "Sep 1": a date with no weekday, for the strip. */
export function shortDay(dateIso: string): string {
  return dayLabel(dateIso).split(" ").slice(1).join(" ");
}

export function basisLabel(basis: WorksheetBasis): string {
  return basis === "business_date" ? "Business date" : "Calendar date";
}

export function basisLowerLabel(basis: WorksheetBasis): string {
  return basis === "business_date" ? "business date" : "calendar date";
}

// ---------------------------------------------------------------------
// Words for rates and the strip
// ---------------------------------------------------------------------

/** 825 -> "8.25%", 725 -> "7.25%", 50 -> "0.50%". */
export function ratePercent(basisPoints: number): string {
  return `${(basisPoints / 100).toFixed(2)}%`;
}

/** "Business day ends at 3:00 AM, Central Time". */
export function businessDayText(cutoffLocal: string, friendlyZone: string): string {
  const cutoff = cutoffText(cutoffLocal);
  return cutoff === "midnight"
    ? `Business day ends at midnight, ${friendlyZone}`
    : `Business day ends at ${cutoff}, ${friendlyZone}`;
}

export interface StripPill {
  text: string;
  tone: "info" | "final" | "warn";
}

/** The strip's status pills: simulated payments, and whether the period is final. */
export function stripPills(
  worksheet: SalesTaxWorksheetDto,
  context: SalesTaxContextDto,
): StripPill[] {
  const pills: StripPill[] = [];
  if (context.paymentsSimulated) {
    pills.push({ text: "Simulated payments", tone: "info" });
  }
  const days = worksheet.days.length;
  const finalized = worksheet.checks.tiesToCloseouts.finalizedDaysChecked;
  const wholeLabel = periodNoun(context.period.kind);
  if (worksheet.to >= context.today) {
    pills.push({ text: `${shortDay(context.today)} is not final yet`, tone: "warn" });
  } else if (finalized >= days) {
    pills.push({ text: `${wholeLabel} is closed`, tone: "final" });
  } else {
    const open = days - finalized;
    pills.push({
      text: `${open} ${open === 1 ? "day is" : "days are"} not closed out yet`,
      tone: "warn",
    });
  }
  return pills;
}

/** The tax-due caption: one rate, or each rate and the day it started. */
export function totalDueCaption(
  worksheet: SalesTaxWorksheetDto,
  context: SalesTaxContextDto,
): string {
  const changes = worksheet.checks.rateChangedMidPeriod.changes;
  const first = changes[0];
  if (first !== undefined && first.fromBasisPoints !== null) {
    const parts = [`${ratePercent(first.fromBasisPoints)} before ${shortDay(first.effectiveFrom)}`];
    for (const change of changes) {
      parts.push(`${ratePercent(change.toBasisPoints)} from ${shortDay(change.effectiveFrom)}`);
    }
    return parts.join(", ");
  }
  const total = context.rateComponentsAtEnd.reduce((sum, entry) => sum + entry.basisPoints, 0);
  return total > 0 ? `${ratePercent(total)} on taxable receipts` : "On taxable receipts";
}

// ---------------------------------------------------------------------
// The E-500 card
// ---------------------------------------------------------------------

export interface E500Row {
  key: string;
  kind: "line" | "group" | "note";
  /** "Line 12" where the line is confirmed against the form, otherwise empty: no badge is drawn. */
  line: string;
  title: string;
  caption: string;
  amount: string;
  tone: "normal" | "minor" | "key";
  /** True for the one figure that can be negative and is called out when it is. */
  negative?: boolean;
}

const FORM_COMPONENTS = ["state", "county", "transit", "additional_county"] as const;
// ONBW1-S2: city and special district are shown only where the location's rate has them or money was recorded
// against them, so a location without them sees the same four lines as before. Neither has a form line number.
const EXTRA_COMPONENTS = ["city", "special_district"] as const;
const COMPONENT_TITLES: Record<(typeof FORM_COMPONENTS)[number] | (typeof EXTRA_COMPONENTS)[number], string> = {
  state: "State tax",
  county: "County tax",
  transit: "Transit tax",
  additional_county: "Additional county tax",
  city: "City tax",
  special_district: "Special district tax",
};

export function excessCaption(excessCents: number): string {
  return excessCents < 0
    ? "Below zero: you collected less than the total due. See the Checks."
    : "Collected minus total tax due. Usually pennies from rounding.";
}

export function buildE500Rows(
  worksheet: SalesTaxWorksheetDto,
  context: SalesTaxContextDto,
): E500Row[] {
  const t = worksheet.totals;
  const atEnd = new Map(
    context.rateComponentsAtEnd.map((entry) => [entry.component, entry.basisPoints]),
  );
  const changed = worksheet.checks.rateChangedMidPeriod.changed;
  const rows: E500Row[] = [
    {
      key: "gross",
      kind: "line",
      line: lineBadge("gross"),
      title: "Gross receipts",
      caption: "Sales after discounts and comps. No tax, no tips.",
      amount: formatMoney(t.grossReceiptsCents),
      tone: "normal",
    },
    {
      key: "exempt",
      kind: "line",
      line: lineBadge("exempt"),
      title: "Exempt receipts",
      caption: "Nothing is tracked as exempt yet",
      amount: formatMoney(t.exemptReceiptsCents),
      tone: "normal",
    },
    {
      key: "refunds",
      kind: "line",
      line: "",
      title: "Less refunds",
      caption: "Refunds count in the period they were given back",
      amount: formatMoney(-t.refundsCents),
      tone: "minor",
    },
    {
      key: "taxable",
      kind: "line",
      line: lineBadge("taxable"),
      title: "Taxable receipts",
      caption: "Gross receipts, minus exempt, minus refunds",
      amount: formatMoney(t.taxableReceiptsCents),
      tone: "key",
    },
    {
      key: "parts",
      kind: "group",
      line: "",
      title: "Tax by part",
      caption: "",
      amount: "",
      tone: "normal",
    },
  ];
  const shownComponents = [
    ...FORM_COMPONENTS,
    ...EXTRA_COMPONENTS.filter(
      (component) =>
        atEnd.has(component) ||
        t.taxDueByComponent[component] !== 0 ||
        t.collectedByComponent[component] !== 0,
    ),
  ];
  for (const component of shownComponents) {
    const basisPoints = atEnd.get(component);
    let caption: string;
    if (basisPoints === undefined) {
      caption = "Not part of this location's rate";
    } else {
      caption = changed
        ? `${ratePercent(basisPoints)} at the end of these dates`
        : ratePercent(basisPoints);
    }
    rows.push({
      key: component,
      kind: "line",
      line: component === "city" || component === "special_district" ? "" : lineBadge(component),
      title: COMPONENT_TITLES[component],
      caption,
      amount: formatMoney(t.taxDueByComponent[component]),
      tone: "normal",
    });
  }
  const hasUnsplit =
    t.taxDueByComponent.unsplit !== 0 ||
    t.collectedByComponent.unsplit !== 0 ||
    atEnd.has("unsplit");
  if (hasUnsplit) {
    rows.push({
      key: "unsplit",
      kind: "line",
      line: "",
      title: "Tax at the combined rate",
      caption:
        "This rate is not divided into state, county, transit and additional county. Record the parts in Settings, Sales tax, before you fill in lines 9 to 12.",
      amount: formatMoney(t.taxDueByComponent.unsplit),
      tone: "normal",
    });
  }
  rows.push(
    {
      key: "totalDue",
      kind: "line",
      line: lineBadge("totalDue"),
      title: "Total tax due",
      caption: totalDueCaption(worksheet, context),
      amount: formatMoney(t.totalTaxDueCents),
      tone: "key",
    },
    {
      key: "collected",
      kind: "line",
      line: "",
      title: "Tax you actually collected",
      caption: "Tax charged on sales, minus tax given back on refunds",
      amount: formatMoney(t.netTaxCollectedCents),
      tone: "minor",
    },
    {
      key: "excess",
      kind: "line",
      line: lineBadge("excess"),
      title: "Excess tax collected",
      caption: excessCaption(t.excessCollectedCents),
      amount: formatMoney(t.excessCollectedCents),
      tone: "key",
      negative: t.excessCollectedCents < 0,
    },
  );
  return rows;
}

// ---------------------------------------------------------------------
// The two ways to count
// ---------------------------------------------------------------------

export interface WaysToCount {
  business: { taxableCents: number; taxDueCents: number };
  calendar: { taxableCents: number; taxDueCents: number };
  /** Calendar date minus business date, in tax. */
  differenceText: string;
  selected: WorksheetBasis;
}

/** Null when the two bases agree: the card is only shown when they differ. */
export function buildWaysToCount(
  worksheet: SalesTaxWorksheetDto,
  _context: SalesTaxContextDto,
): WaysToCount | null {
  if (!worksheet.otherBasis.differs) {
    return null;
  }
  const own = {
    taxableCents: worksheet.totals.taxableReceiptsCents,
    taxDueCents: worksheet.totals.totalTaxDueCents,
  };
  const other = {
    taxableCents: worksheet.otherBasis.taxableReceiptsCents,
    taxDueCents: worksheet.otherBasis.totalTaxDueCents,
  };
  const business = worksheet.basis === "business_date" ? own : other;
  const calendar = worksheet.basis === "business_date" ? other : own;
  return {
    business,
    calendar,
    selected: worksheet.basis,
    differenceText: `${formatMoney(calendar.taxDueCents - business.taxDueCents)} of tax`,
  };
}

// ---------------------------------------------------------------------
// Checks, in plain words
// ---------------------------------------------------------------------

export type CheckState = "ok" | "warn" | "info";

export interface CheckRow {
  id: string;
  state: CheckState;
  title: string;
  detail: string;
  /** The words on the 44px button that opens the list, when there is a list to show. */
  listButton?: string;
  /** Plain lines for the list: the days, then the reference codes. */
  listLines?: string[];
}

const DAY_FLAG_FOR_CHECK: Record<string, DayFlag> = {
  paymentsMissingTax: "payment_missing_tax",
  paymentsWithoutStoredRate: "no_stored_rate",
  refundsMissingTax: "refund_missing_tax",
  unknownRefunds: "unknown_refund",
};

const CLOSEOUT_FIELD_WORDS: Record<string, string> = {
  gross_before_tax: "sales before tax",
  tax: "tax",
  refund_amount: "refunds",
  refund_tax: "refund tax",
};

const DIFFERENCE_FIELD_WORDS: Record<string, string> = {
  gross_receipts: "gross receipts",
  tax_collected: "tax collected",
  refunds: "refunds",
  tax_refunded: "tax refunded",
  components_vs_tax: "the tax parts against the tax charged",
};

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** The first eight characters of an id, the reference code a person can read out. */
export function referenceCode(id: string): string {
  return id.replace(/-/g, "").slice(0, 8);
}

function listFor(
  worksheet: SalesTaxWorksheetDto,
  checkName: string,
  rows: CheckedRowsDto,
): string[] {
  const lines: string[] = [];
  const flag = DAY_FLAG_FOR_CHECK[checkName];
  if (flag !== undefined) {
    const days = worksheet.days.filter((day) => day.flags.includes(flag));
    if (days.length > 0) {
      lines.push(`Days: ${days.map((day) => dayLabel(day.date)).join(", ")}`);
    }
  }
  if (rows.ids.length > 0) {
    lines.push(`Reference codes: ${rows.ids.map(referenceCode).join(", ")}`);
  }
  const more = rows.count - rows.ids.length;
  if (more > 0) {
    lines.push(`and ${more} more not listed`);
  }
  return lines;
}

function countCheck(
  worksheet: SalesTaxWorksheetDto,
  name: keyof typeof DAY_FLAG_FOR_CHECK | "unresolvedPayments" | "unresolvedRefunds",
  rows: CheckedRowsDto,
  words: {
    okTitle: string;
    okDetail: string;
    warnTitle: (count: number) => string;
    warnDetail: (amount: string) => string;
    button: (count: number) => string;
  },
): CheckRow {
  if (rows.count === 0) {
    return { id: name, state: "ok", title: words.okTitle, detail: words.okDetail };
  }
  return {
    id: name,
    state: "warn",
    title: words.warnTitle(rows.count),
    detail: words.warnDetail(formatMoney(rows.amountCents)),
    listButton: words.button(rows.count),
    listLines: listFor(worksheet, name, rows),
  };
}

export function buildChecks(
  worksheet: SalesTaxWorksheetDto,
  context: SalesTaxContextDto,
  friendlyZone: string,
): CheckRow[] {
  const c = worksheet.checks;
  const rows: CheckRow[] = [];

  // The time zone every date was worked in, always stated.
  if (c.timeZone.isUtc) {
    rows.push({
      id: "timeZone",
      state: "warn",
      title: "The time zone is not set",
      detail:
        c.timeZone.warning ??
        "This location's time zone is UTC, the default. Confirm it before relying on this worksheet.",
    });
  } else {
    rows.push({
      id: "timeZone",
      state: "ok",
      title: `Dates are worked in ${friendlyZone}`,
      detail: `${businessDayText(context.cutoffLocal, friendlyZone)}. A sale is dated by the day the card was charged.`,
    });
  }

  // Ties to closeouts.
  const closeouts = c.tiesToCloseouts;
  if (closeouts.mismatches.length > 0) {
    const days = [...new Set(closeouts.mismatches.map((m) => m.businessDate))];
    rows.push({
      id: "tiesToCloseouts",
      state: "warn",
      title: `${days.length} ${plural(days.length, "business day does", "business days do")} not match the Closeout Summary`,
      detail:
        "The closeout stored for the day and the payments disagree. Open the list to see which figures.",
      listButton: `See the ${days.length} ${plural(days.length, "day", "days")}`,
      listLines: closeouts.mismatches.map(
        (m) =>
          `${dayLabel(m.businessDate)}: ${CLOSEOUT_FIELD_WORDS[m.field] ?? m.field} is ${
            m.closeoutCents === null ? "missing" : formatMoney(m.closeoutCents)
          } on the closeout and ${formatMoney(m.computedCents)} from payments`,
      ),
    });
  } else if (closeouts.daysWithoutRefundTax.length > 0) {
    const n = closeouts.daysWithoutRefundTax.length;
    rows.push({
      id: "tiesToCloseouts",
      state: "warn",
      title: `${n} finalized ${plural(n, "day has", "days have")} no refund tax on the closeout`,
      detail:
        "Closeouts finalized before refund tax was recorded carry none, so those days cannot be compared in full.",
      listButton: `See the ${n} ${plural(n, "day", "days")}`,
      listLines: [`Days: ${closeouts.daysWithoutRefundTax.map(dayLabel).join(", ")}`],
    });
  } else if (closeouts.finalizedDaysChecked === 0) {
    rows.push({
      id: "tiesToCloseouts",
      state: "info",
      title: "Nothing to compare with the closeouts yet",
      detail:
        "No business day in these dates is finalized, so no Closeout Summary exists to check against.",
    });
  } else {
    const n = closeouts.finalizedDaysChecked;
    rows.push({
      id: "tiesToCloseouts",
      state: "ok",
      title: "Ties to closeouts",
      detail: `${n === 1 ? "The 1 finalized business day matches" : `All ${n} finalized business days match`} the Closeout Summary.`,
    });
  }

  // Ties to payments.
  if (c.tiesToPayments.pass) {
    rows.push({
      id: "tiesToPayments",
      state: "ok",
      title: "Ties to payments",
      detail: "Sales and tax on every day add up to the payments and refunds taken.",
    });
  } else {
    rows.push({
      id: "tiesToPayments",
      state: "warn",
      title: "The days do not add up to the payments",
      detail:
        "Do not rely on this worksheet until this is explained. Open the list to see which figures.",
      listButton: "See the figures",
      listLines: c.tiesToPayments.differences.map(
        (d) =>
          `${DIFFERENCE_FIELD_WORDS[d.field] ?? d.field}: days add to ${formatMoney(d.dayRowsCents)}, payments to ${formatMoney(d.paymentsCents)}`,
      ),
    });
  }

  rows.push(
    countCheck(worksheet, "paymentsMissingTax", c.paymentsMissingTax, {
      okTitle: "No payment is missing tax",
      okDetail: "Every payment has a tax amount.",
      warnTitle: (n) => `${n} ${plural(n, "payment is", "payments are")} missing tax`,
      warnDetail: (amount) => `They total ${amount}. Tax collected may be short for them.`,
      button: (n) => `See the ${n} ${plural(n, "payment", "payments")}`,
    }),
    countCheck(worksheet, "paymentsWithoutStoredRate", c.paymentsWithoutStoredRate, {
      okTitle: "Every payment has its rate on record",
      okDetail: "Each payment stored the rate it was charged at.",
      warnTitle: (n) => `${n} ${plural(n, "payment has", "payments have")} no rate on record`,
      warnDetail: (amount) =>
        `They total ${amount} in tax. The worksheet uses the rate in effect on the day for them, and the day is marked.`,
      button: (n) => `See the ${n} ${plural(n, "payment", "payments")}`,
    }),
    countCheck(worksheet, "refundsMissingTax", c.refundsMissingTax, {
      okTitle: "Refunds all have tax on record",
      okDetail:
        worksheet.totals.refundsCents === 0 && worksheet.totals.taxRefundedCents === 0
          ? "There are no refunds in these dates."
          : "Every refund shows the tax that was given back.",
      warnTitle: (n) => `${n} ${plural(n, "refund has", "refunds have")} no tax on record`,
      warnDetail: (amount) =>
        `They total ${amount}. The worksheet uses the rate in effect on the day for them.`,
      button: (n) => `See the ${n} ${plural(n, "refund", "refunds")}`,
    }),
    countCheck(worksheet, "unknownRefunds", c.unknownRefunds, {
      okTitle: "No refund has an unknown outcome",
      okDetail: "Every refund is either done or failed.",
      warnTitle: (n) => `${n} ${plural(n, "refund has", "refunds have")} an unknown outcome`,
      warnDetail: (amount) =>
        `They total ${amount}. The card network has not said whether the money went back. They are counted until you know.`,
      button: (n) => `See the ${n} ${plural(n, "refund", "refunds")}`,
    }),
    countCheck(worksheet, "unresolvedPayments", c.unresolvedPayments, {
      okTitle: "No payment is still waiting",
      okDetail: "Every payment in these dates has settled.",
      warnTitle: (n) => `${n} ${plural(n, "payment is", "payments are")} not settled yet`,
      warnDetail: (amount) =>
        `They total ${amount} and are outside every figure on this page until they settle.`,
      button: (n) => `See the ${n} ${plural(n, "payment", "payments")}`,
    }),
    countCheck(worksheet, "unresolvedRefunds", c.unresolvedRefunds, {
      okTitle: "No refund is still waiting",
      okDetail: "Every refund in these dates has settled.",
      warnTitle: (n) => `${n} ${plural(n, "refund is", "refunds are")} not settled yet`,
      warnDetail: (amount) =>
        `They total ${amount} and are outside every figure on this page until they settle.`,
      button: (n) => `See the ${n} ${plural(n, "refund", "refunds")}`,
    }),
  );

  // The rate.
  const rate = c.rateChangedMidPeriod;
  if (rate.changes.length > 0) {
    const first = rate.changes[0];
    const last = rate.changes[rate.changes.length - 1];
    const lines = rate.changes.map(
      (change) =>
        `${ratePercent(change.toBasisPoints)} from ${shortDay(change.effectiveFrom)}${
          change.fromBasisPoints === null ? "" : `, was ${ratePercent(change.fromBasisPoints)}`
        }`,
    );
    rows.push({
      id: "rateChanged",
      state: "warn",
      title:
        rate.changes.length === 1 && first !== undefined
          ? `The rate changed on ${shortDay(first.effectiveFrom)}`
          : `The rate changed ${rate.changes.length} times`,
      detail:
        last === undefined
          ? ""
          : `${lines.join(". ")}. A form covers one rate, and most filers file each rate period separately.`,
    });
  } else if (rate.distinctStoredRates.length > 1) {
    rows.push({
      id: "rateChanged",
      state: "warn",
      title: "More than one rate is in these figures",
      detail: `Payments or refunds in these dates were charged at ${rate.distinctStoredRates
        .map(ratePercent)
        .join(" and ")}, for example a refund of an earlier sale. Tax due is worked at each rate.`,
    });
  } else {
    const dayRate = worksheet.days.find((day) => day.rateBasisPoints !== null)?.rateBasisPoints;
    rows.push({
      id: "rateChanged",
      state: "ok",
      title: "Rate did not change",
      detail:
        dayRate === undefined || dayRate === null
          ? "The rate was the same on every day."
          : `${ratePercent(dayRate)} every day.`,
    });
  }
  return rows;
}

export interface CheckSummary {
  text: string;
  tone: "ok" | "info" | "warn";
  problems: number;
}

/** "All clear" only when nothing is a problem AND nothing was left uncompared; "Nothing wrong found" when
 * the only open rows are neutral; "N to look at" when any check is a problem. */
export function checkSummary(checks: CheckRow[]): CheckSummary {
  const problems = checks.filter((row) => row.state === "warn").length;
  const neutral = checks.filter((row) => row.state === "info").length;
  if (problems > 0) return { text: `${problems} to look at`, tone: "warn", problems };
  if (neutral > 0) return { text: "Nothing wrong found", tone: "info", problems };
  return { text: "All clear", tone: "ok", problems };
}

/** Passing checks collapse into one tappable line; problems and neutral rows stay open. */
export function passedChecksLabel(count: number): string {
  return `${count} ${count === 1 ? "check" : "checks"} passed`;
}

// ---------------------------------------------------------------------
// Day by day
// ---------------------------------------------------------------------

const DAY_FLAG_WORDS: Record<DayFlag, string> = {
  payment_missing_tax: "Payment with no tax",
  no_stored_rate: "Payment with no rate on record",
  refund_missing_tax: "Refund, tax not stored",
  unknown_refund: "Refund outcome unknown",
  rate_changed: "New rate starts",
};

export function dayFlagWords(flags: DayFlag[]): string {
  return flags.map((flag) => DAY_FLAG_WORDS[flag]).join(", ");
}

export interface DayRowView {
  date: string;
  label: string;
  note: string;
  highlighted: boolean;
  cells: {
    gross: string;
    discounts: string;
    refunds: string;
    taxable: string;
    collected: string;
    refunded: string;
    net: string;
    rate: string;
  };
  zero: { discounts: boolean; refunds: boolean; refunded: boolean };
}

function dayRateText(basisPoints: number | null): string {
  return basisPoints === null ? "-" : ratePercent(basisPoints);
}

function reduction(cents: number): string {
  return formatMoney(cents === 0 ? 0 : -Math.abs(cents));
}

/** TAX2-S2-F3. The rate to show for one day. The day's own rate when the worksheet has one. A
 * location that never recorded a rate set holds a single combined rate (one `unsplit` component) that
 * the worksheet reports no per-day figure for; that one rate was in effect on every day of the period
 * as long as no change is recorded in it, so it is shown. Anything else stays null (the cell shows
 * "-"): a split location's day with no rate, a period with a recorded rate change or more than one
 * stored rate, an unsplit rate of zero. Never a rate worked out from the end-of-period split. */
export function effectiveDayRate(
  day: WorksheetDayDto,
  worksheet: SalesTaxWorksheetDto,
  context: SalesTaxContextDto,
): number | null {
  if (day.rateBasisPoints !== null) {
    return day.rateBasisPoints;
  }
  const only =
    context.rateComponentsAtEnd.length === 1 ? context.rateComponentsAtEnd[0] : undefined;
  if (only === undefined || only.component !== "unsplit" || only.basisPoints <= 0) {
    return null;
  }
  const rate = worksheet.checks.rateChangedMidPeriod;
  if (rate.changed || rate.changes.length > 0 || rate.distinctStoredRates.length > 1) {
    return null;
  }
  return only.basisPoints;
}

export function buildDayRows(
  worksheet: SalesTaxWorksheetDto,
  context: SalesTaxContextDto,
): DayRowView[] {
  return worksheet.days.map((day) => ({
    date: day.date,
    label: dayLabel(day.date),
    note: dayFlagWords(day.flags),
    highlighted: day.flags.length > 0,
    cells: {
      gross: formatMoney(day.grossReceiptsCents),
      discounts: reduction(day.discountsAndCompsCents),
      refunds: reduction(day.refundsCents),
      taxable: formatMoney(day.taxableReceiptsCents),
      collected: formatMoney(day.taxCollectedCents),
      refunded: reduction(day.taxRefundedCents),
      net: formatMoney(day.netTaxCents),
      rate: dayRateText(effectiveDayRate(day, worksheet, context)),
    },
    zero: {
      discounts: day.discountsAndCompsCents === 0,
      refunds: day.refundsCents === 0,
      refunded: day.taxRefundedCents === 0,
    },
  }));
}

/** The Total row. The rate cell reads "7.25% to 8.25%" when the days differ. */
export function buildTotalRow(
  worksheet: SalesTaxWorksheetDto,
  context: SalesTaxContextDto,
): DayRowView["cells"] {
  const t = worksheet.totals;
  const rates = worksheet.days
    .map((day) => effectiveDayRate(day, worksheet, context))
    .filter((rate): rate is number => rate !== null);
  let rate = "-";
  if (rates.length > 0) {
    const low = Math.min(...rates);
    const high = Math.max(...rates);
    rate = low === high ? ratePercent(low) : `${ratePercent(low)} to ${ratePercent(high)}`;
  }
  return {
    gross: formatMoney(t.grossReceiptsCents),
    discounts: reduction(t.discountsAndCompsCents),
    refunds: reduction(t.refundsCents),
    taxable: formatMoney(t.taxableReceiptsCents),
    collected: formatMoney(t.taxCollectedCents),
    refunded: reduction(t.taxRefundedCents),
    net: formatMoney(t.netTaxCollectedCents),
    rate,
  };
}

// ---------------------------------------------------------------------
// Refusals, in plain words
// ---------------------------------------------------------------------

export interface WorksheetRefusal {
  kind: "no-access" | "multi-location" | "failed";
  title: string;
  body: string;
  /** One plain next step, where there is one. */
  nextStep?: string;
}

/** The label the server gives the permission this worksheet needs, for the no-access message. */
export const SALES_TAX_PERMISSION_LABEL = "See the sales tax worksheet";

export function describeWorksheetRefusal(
  status: number,
  errorCode: string | undefined,
  serverMessage: string | undefined,
): WorksheetRefusal {
  if (status === 403) {
    return {
      kind: "no-access",
      title: "You do not have access to this report",
      body: `Ask an owner or manager to turn on “${SALES_TAX_PERMISSION_LABEL}” for your role.`,
    };
  }
  if (status === 409 && errorCode === "tax_worksheet_multi_location_unsupported") {
    return {
      kind: "multi-location",
      title: "This worksheet is for one location",
      body: "This account has more than one active location, and sales are not recorded by location yet, so a worksheet for one location cannot be produced.",
      nextStep: "Ask whoever set up your account to split the locations.",
    };
  }
  return {
    kind: "failed",
    title: "Could not load the Sales tax worksheet.",
    body: serverMessage === undefined || serverMessage === "" ? "" : serverMessage,
  };
}

/** The "How this is counted" list, from the design. */
export const HOW_COUNTED: readonly string[] = [
  "Tips are not receipts and are left out.",
  "Gift card sales are not taxable. Gift cards used to pay are.",
  "House discounts and member prices lower the taxable amount. Comps count as no sale.",
  "A refund lowers taxable receipts on the day it was given, not the day of the sale.",
];

export const WORKSHEET_NEVER_FILES =
  "A worksheet to help you or your accountant fill in your sales tax return. It does not file anything.";
